import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  ImageList,
  ImageListItem,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import PhotoAnnotator from '../../../components/PhotoAnnotator';
import DynamicConfirmModal from '../../../common/DynamicConfirmModal';
import { AddIcon, XIcon } from '../../../components/GamifyIcon';
import Dropzone from 'react-dropzone';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import * as mutations from '../../../../graphql/mutations';
import DisplayMedia from '../../../components/DisplayMedia';
import { GamifyToast } from '../../../common/CustomToasts';
import {
  generateRandomImageFileNameString,
  newLocalToISODateFormat,
  getLocalDateTime,
} from '../../../common/Utilities';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import featureFlag from '../../../common/featureFlag';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface ProcessedImage {
  imageName: string;
  tags: string[];
  description: string;
  index: number;
  sequence: number;
  createdAt: string | null;
  type?: string;
  fileId?: string; // ID from deal.files.items for proper deletion
}

interface DealPhotosProps {
  photos: any[]; // array of photo objects with metadata from deal.files.items
  deal: any;
  isImageUploading: boolean;
  setIsImageUploading: (uploading: boolean) => void;
  onTriggerSelectedDealRefetch: () => void;
  handleRefetchDeals: (
    stageIds?: string[],
    sortOrder?: any,
    showLoading?: boolean
  ) => void;
  handleImageClick: (index: number) => void;
  isOnKanban: boolean;
  showEdit: boolean;
  onImagesChange?: (images: ProcessedImage[]) => void;
}

export default function DealPhotos({
  photos,
  deal,
  isImageUploading,
  setIsImageUploading,
  onTriggerSelectedDealRefetch,
  handleRefetchDeals,
  handleImageClick,
  isOnKanban,
  showEdit,
  onImagesChange,
}: DealPhotosProps) {
  const { theme } = useTheme();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Check PaivVideo feature flag
  const isPaivVideoEnabled = featureFlag('PaivVideo');
  const [localImages, setLocalImages] = useState<any[] | null>(null);
  const [deletingImageIndex, setDeletingImageIndex] = useState<number | null>(
    null
  );
  const [showConfirmDeleteImage, setShowConfirmDeleteImage] = useState(false);
  const [imageIndexToDelete, setImageIndexToDelete] = useState<number | null>(
    null
  );

  // PDF viewer state
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfTitle, setPdfTitle] = useState<string>('');

  // Video viewer state
  const [showVideoViewer, setShowVideoViewer] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoTitle, setVideoTitle] = useState<string>('');

  // Photo annotator state
  const [showPhotoAnnotator, setShowPhotoAnnotator] = useState(false);
  const [annotatorPhoto, setAnnotatorPhoto] = useState<{
    imageName: string;
    tags: string[];
    description: string;
    fileId?: string;
    type?: string;
  } | null>(null);

  // Reset local images when deal changes to sync with server data
  useEffect(() => {
    setLocalImages(null);
  }, [deal?.id]);

  // Helper function to detect file type from filename
  const getFileTypeFromName = (fileName: string): string => {
    if (!fileName) return 'image/jpeg';

    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'jpeg':
      case 'jpg':
        return 'image/jpeg';
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      case 'mkv':
        return 'video/x-matroska';
      default:
        return 'image/jpeg';
    }
  };

  // Helper function to detect if file is a video
  const isVideo = (file: ProcessedImage | any): boolean => {
    if (!file) return false;
    const fileType =
      file.type ||
      file.fileType ||
      getFileTypeFromName(file.imageName || file.fileName || '');
    return (
      fileType.startsWith('video/') ||
      (file.imageName && /\.(mp4|mov|avi|mkv)$/i.test(file.imageName))
    );
  };

  // Helper function to check if a string is a valid image/video/pdf filename
  const isValidMediaFileName = (fileName: string): boolean => {
    if (!fileName || typeof fileName !== 'string') return false;
    const ext = fileName.split('.').pop()?.toLowerCase();
    const validExtensions = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'pdf',
      'mp4',
      'mov',
      'avi',
      'mkv',
    ];
    return ext ? validExtensions.includes(ext) : false;
  };

  // Process photos into separate sections like mobile (New, Uploaded, Uploading)
  const processPhotoSections = () => {
    try {
      // Section 1: New/Local Images (immediate UI feedback)
      const newImages: ProcessedImage[] = localImages
        ? localImages
            .filter(
              (imageName: string) =>
                imageName &&
                imageName !== 'undefined' &&
                imageName !== 'null' &&
                !imageName.includes('undefined')
            )
            .map(
              (imageName: string, index: number): ProcessedImage => ({
                imageName,
                tags: [],
                description:
                  getFileTypeFromName(imageName) === 'application/pdf'
                    ? 'PDF Document'
                    : isPaivVideoEnabled &&
                      getFileTypeFromName(imageName).startsWith('video/')
                    ? 'Video'
                    : 'Photo',
                index,
                sequence: index,
                createdAt: new Date().toISOString(),
                type: getFileTypeFromName(imageName),
              })
            )
        : [];

      // Get image names for filtering
      const newImageNames = new Set(newImages.map((img) => img.imageName));

      // Parse flexAttributes once to avoid multiple parsing
      const flexAttributes = JSON.parse(deal?.flexAttributes || '{}');

      // Extract ALL photos from flexAttributes ImageList fields
      // This includes individual fields like photosOfEverythingAdjusterNoted, utilityBill, etc.
      const allFlexPhotos: { imageName: string; tag: string }[] = [];
      for (const [fieldName, fieldValue] of Object.entries(flexAttributes)) {
        if (Array.isArray(fieldValue)) {
          for (const item of fieldValue) {
            if (typeof item === 'string' && isValidMediaFileName(item)) {
              allFlexPhotos.push({ imageName: item, tag: fieldName });
            }
          }
        }
      }

      const flexImageNames = new Set(allFlexPhotos.map((p) => p.imageName));

      // SYNCHRONIZATION: Add any missing deal.files.items to flexAttributes
      // This ensures mobile and web are in sync
      if (
        deal.files &&
        deal.files.items &&
        Array.isArray(deal.files.items) &&
        !localImages
      ) {
        // Only do this if we're not in the middle of an operation
        const missingFiles = deal.files.items.filter(
          (file: any) =>
            file &&
            !file.isDeleted &&
            file.id &&
            !flexImageNames.has(file.id) &&
            !flexImageNames.has(file.fileName) &&
            !flexImageNames.has(file.url)
        );

        if (missingFiles.length > 0) {
          // Files exist in deal.files.items but not in flexAttributes
          // This is normal and will be handled during deletion if needed
        }
      }

      // Section 2: Uploaded Photos (from deal.files.items, excluding new images)
      // Filter out deleted files first, then apply other filters
      const uploadedFromFiles: ProcessedImage[] = photos
        .filter(
          (file: any) =>
            file &&
            file.isDeleted !== true && // Explicitly check for not deleted
            // Measurement/estimate report documents are deal FILES, not
            // photos — they belong in the Claim tab / file lists, not the
            // photo grid (GG-3433 D1).
            !['measurement_report', 'estimate'].includes(file.category) &&
            file.type &&
            (/^image\//i.test(file.type) ||
              file.type === 'application/pdf' ||
              (isPaivVideoEnabled && file.type.startsWith('video/'))) &&
            file.id &&
            (file.url || file.fileName || file.id) && // Ensure we have a valid file reference
            !newImageNames.has(file.url || file.fileName || file.id)
        )
        .sort((a: any, b: any) => (a.sequence || 0) - (b.sequence || 0))
        .map((file: any, index: number): ProcessedImage => {
          const imageName = file.url || file.fileName || file.id;
          return {
            imageName,
            tags: Array.isArray(file.tags) ? file.tags : [],
            description:
              file.description ||
              (file.type === 'application/pdf'
                ? 'PDF Document'
                : isPaivVideoEnabled && file.type.startsWith('video/')
                ? 'Video'
                : 'Photo'),
            index,
            sequence: file.sequence || 0,
            createdAt: file.createdAt,
            type: file.type,
            fileId: file.id, // Store the file ID for deletion
          };
        })
        .filter(
          (img: ProcessedImage) =>
            img.imageName &&
            img.imageName !== 'undefined' &&
            img.imageName !== 'null' &&
            !img.imageName.includes('undefined')
        );

      const uploadedFromFilesNames = new Set(
        uploadedFromFiles.map((img) => img.imageName)
      );

      // Also include photos from flexAttributes ImageList fields
      // These are "uploaded" photos that were added via ImageList fields in the form
      const uploadedFromFlex: ProcessedImage[] = allFlexPhotos
        .filter(
          (p) =>
            !newImageNames.has(p.imageName) &&
            !uploadedFromFilesNames.has(p.imageName)
        )
        .map(
          (p, index): ProcessedImage => ({
            imageName: p.imageName,
            tags: [p.tag],
            description:
              getFileTypeFromName(p.imageName) === 'application/pdf'
                ? 'PDF Document'
                : isPaivVideoEnabled &&
                  getFileTypeFromName(p.imageName).startsWith('video/')
                ? 'Video'
                : 'Photo',
            index: uploadedFromFiles.length + index,
            sequence: uploadedFromFiles.length + index,
            createdAt: null,
            type: getFileTypeFromName(p.imageName),
          })
        );

      const uploadedImages = [...uploadedFromFiles, ...uploadedFromFlex];
      const uploadedImageNames = new Set(
        uploadedImages.map((img) => img.imageName)
      );

      // Section 3: Uploading Photos (from flexAttributes.photo, excluding new and uploaded)
      // Note: flexAttributes.photo is used for the general "upload" flow
      let uploadingImages: ProcessedImage[] = [];
      try {
        const photoArray = flexAttributes.photo || [];
        if (photoArray.length > 0) {
          uploadingImages = photoArray
            .filter(
              (imageName: string) =>
                imageName &&
                imageName !== 'undefined' &&
                imageName !== 'null' &&
                !imageName.includes('undefined') &&
                !newImageNames.has(imageName) &&
                !uploadedImageNames.has(imageName)
            )
            .map(
              (imageName: string, index: number): ProcessedImage => ({
                imageName,
                tags: [],
                description:
                  getFileTypeFromName(imageName) === 'application/pdf'
                    ? 'PDF Document'
                    : isPaivVideoEnabled &&
                      getFileTypeFromName(imageName).startsWith('video/')
                    ? 'Video'
                    : 'Photo',
                index,
                sequence: index,
                createdAt: null,
                type: getFileTypeFromName(imageName),
              })
            );
        }
      } catch (error) {
        console.error('Error parsing flexAttributes:', error);
      }

      return { newImages, uploadedImages, uploadingImages };
    } catch (error) {
      console.error('Error processing photo sections:', error);
      return { newImages: [], uploadedImages: [], uploadingImages: [] };
    }
  };

  const localImagesKey = localImages?.join(',') ?? '';

  const { newImages, uploadedImages, uploadingImages } = useMemo(() => {
    return processPhotoSections();
  }, [
    deal?.id,
    deal?.updatedAt,
    deal?.flexAttributes,
    photos.length,
    localImagesKey,
  ]);

  // Combine all images for backward compatibility with existing logic
  const processedImages = useMemo(() => {
    const combined = [...newImages, ...uploadedImages, ...uploadingImages];

    // Debug logging to identify undefined images
    const undefinedImages = combined.filter(
      (img) => !img.imageName || img.imageName === 'undefined'
    );
    if (undefinedImages.length > 0) {
      console.warn('Found undefined images:', undefinedImages);
    }

    // Final filter to ensure no undefined images make it through
    return combined.filter(
      (img) =>
        img.imageName &&
        img.imageName !== 'undefined' &&
        img.imageName !== 'null' &&
        !img.imageName.includes('undefined')
    );
  }, [newImages, uploadedImages, uploadingImages]);

  // Extract all unique tags from the photos
  const allTags = Array.from(
    new Set([
      ...processedImages.flatMap((img: ProcessedImage) => img.tags || []),
    ])
  ).filter((tag) => tag && tag.length > 0);

  // Apply tag filtering to each section
  const filteredNewImages = selectedTag
    ? newImages.filter(
        (img: ProcessedImage) => img.tags && img.tags.includes(selectedTag)
      )
    : newImages;

  const filteredUploadedImages = selectedTag
    ? uploadedImages.filter(
        (img: ProcessedImage) => img.tags && img.tags.includes(selectedTag)
      )
    : uploadedImages;

  const filteredUploadingImages = selectedTag
    ? uploadingImages.filter(
        (img: ProcessedImage) => img.tags && img.tags.includes(selectedTag)
      )
    : uploadingImages;

  const onImagesChangeRef = useRef(onImagesChange);
  onImagesChangeRef.current = onImagesChange;

  useEffect(() => {
    onImagesChangeRef.current?.(processedImages);
  }, [processedImages.length]);

  const handleDeleteImageClick = (imageIndex: number) => {
    setImageIndexToDelete(imageIndex);
    setShowConfirmDeleteImage(true);
  };

  const handleConfirmDeleteImage = async () => {
    if (imageIndexToDelete === null || !processedImages[imageIndexToDelete]) {
      setShowConfirmDeleteImage(false);
      return;
    }

    setDeletingImageIndex(imageIndexToDelete);

    try {
      const flexAttributes = JSON.parse(deal.flexAttributes || '{}');
      const imageToDelete = processedImages[imageIndexToDelete];
      const imageNameToDelete = imageToDelete.imageName;
      const fileId = imageToDelete.fileId;

      console.log('Attempting to delete:', {
        imageName: imageNameToDelete,
        fileId,
        fromSection: fileId ? 'deal.files.items' : 'flexAttributes',
        imageToDeleteObject: imageToDelete,
        dealFilesItems: deal.files?.items?.slice(0, 3), // Show first 3 for debugging
      });

      // Always ensure both arrays exist
      if (!Array.isArray(flexAttributes.photo)) flexAttributes.photo = [];
      if (!Array.isArray(flexAttributes.pictures)) flexAttributes.pictures = [];

      // CRITICAL: If this file is from deal.files.items but not in flexAttributes,
      // we need to add it to flexAttributes first so mobile knows about it when we delete it
      if (
        fileId &&
        !flexAttributes.photo.includes(imageNameToDelete) &&
        !flexAttributes.photo.includes(fileId)
      ) {
        console.log(
          'File exists only in deal.files.items, adding to flexAttributes for mobile sync'
        );
        flexAttributes.photo.push(fileId);
        flexAttributes.pictures.push(fileId);
      }

      // Remove from flexAttributes arrays (this is what mobile reads)
      flexAttributes.photo = flexAttributes.photo.filter(
        (img: string) => img !== imageNameToDelete && img !== fileId
      );
      flexAttributes.pictures = flexAttributes.pictures.filter(
        (img: string) => img !== imageNameToDelete && img !== fileId
      );

      // If this file has a fileId (from deal.files.items), mark it as deleted using updateDealFile
      if (fileId) {
        try {
          console.log(
            'Marking file as deleted in deal.files.items using updateDealFile mutation:',
            fileId
          );

          const updateResult = await API.graphql(
            graphqlOperation(mutations.updateDealFile, {
              input: {
                id: fileId,
                isDeleted: true,
              },
            })
          );
          console.log(
            'Successfully marked file as deleted in database:',
            fileId,
            updateResult
          );
        } catch (updateError) {
          console.error(
            'Error marking file as deleted in database:',
            updateError
          );
          console.error('Update error details:', {
            message: (updateError as any)?.message,
            errors: (updateError as any)?.errors,
            fileId,
          });
          // Continue with flexAttributes deletion even if file update fails
        }
      }

      console.log('Updated flexAttributes after deletion:', {
        photo: flexAttributes.photo,
        pictures: flexAttributes.pictures,
        removedImage: imageNameToDelete,
        removedFileId: fileId,
      });

      // Update local images for immediate UI feedback
      if (localImages) {
        setLocalImages(
          localImages.filter((img: string) => img !== imageNameToDelete)
        );
      } else {
        const currentImages = processedImages.map(
          (img: ProcessedImage) => img.imageName
        );
        setLocalImages(
          currentImages.filter((img: string) => img !== imageNameToDelete)
        );
      }

      // Update deal with newly updated flexAttributes
      deal.flexAttributes = JSON.stringify(flexAttributes);

      const ourDeal = {
        id: deal.id,
        name: deal.name,
        status: deal.status,
        imageName: deal.imageName,
        imageType: deal.imageType,
        currentStageID: deal.currentStageID,
        currentOwnerID: deal.currentOwnerID,
        currentOwnerType: deal.currentOwnerType,
        orgID: deal.orgID,
        isDeleted: false,
        flexAttributes: deal.flexAttributes,
        categoryID: deal.categoryID,
        tmst: newLocalToISODateFormat(getLocalDateTime()),
      };

      await API.graphql(
        graphqlOperation(mutations.maintainDealV3, {
          dealInfo: JSON.stringify(ourDeal),
        })
      );

      GamifyToast.success('File deleted successfully');

      // Clear local images to force re-render from server data
      setLocalImages(null);

      console.log('Deletion completed, triggering refetch...');

      // Trigger refetch to sync with server - add delay to ensure mutations are processed
      setTimeout(() => {
        if (onTriggerSelectedDealRefetch) {
          console.log('Triggering selected deal refetch...');
          onTriggerSelectedDealRefetch();
        }
        if (handleRefetchDeals) {
          console.log('Triggering deals refetch...');
          handleRefetchDeals();
        }
      }, 1000); // 1 second delay to ensure backend has processed the mutations
    } catch (error) {
      console.error('Error deleting file:', error);
      GamifyToast.error('Failed to delete file');

      // Reset local state on error
      setLocalImages(null);
    } finally {
      setDeletingImageIndex(null);
      setShowConfirmDeleteImage(false);
      setImageIndexToDelete(null);
    }
  };

  const onDropDealImage = async (droppedFile: any[]) => {
    setIsImageUploading(true);

    const baseFormats = ['jpeg', 'jpg', 'png', 'gif', 'pdf'];
    const videoFormats = ['mp4', 'mov', 'avi', 'mkv'];
    const formats = isPaivVideoEnabled
      ? [...baseFormats, ...videoFormats]
      : baseFormats;

    const file = droppedFile[0];
    const fileName = file.name;

    if (!formats.includes(fileName.split('.').pop()?.toLowerCase() || '')) {
      const supportedFormats = isPaivVideoEnabled
        ? 'png, gif, jpeg, pdf, mp4, mov, avi, mkv'
        : 'png, gif, jpeg, pdf';
      GamifyToast.error(
        `Deal file format must be one of the following - ${supportedFormats}`
      );
      setIsImageUploading(false);
      return;
    }

    const flexAttributes = JSON.parse(deal.flexAttributes || '{}');
    const fileFormat = fileName.split('.').pop()?.toLowerCase();
    const randomString = generateRandomImageFileNameString();
    const newFileName = `${randomString}.${fileFormat}`;

    try {
      const uploadedFile = await Storage.put(newFileName, file, {
        contentType: file.type,
      });

      if (
        !uploadedFile.key ||
        uploadedFile.key === 'undefined' ||
        uploadedFile.key === 'null' ||
        uploadedFile.key.includes('undefined') ||
        uploadedFile.key.includes('null')
      ) {
        console.error('Invalid file upload detected:', uploadedFile);
        throw new Error('Invalid file upload - undefined or null key detected');
      }

      // Create DealFile object with metadata (similar to mobile's approach)
      const fileObject = {
        id: uploadedFile.key, // Use S3 key as temporary ID
        dealID: deal.id,
        fileName: uploadedFile.key,
        fileType: file.type,
        s3Key: uploadedFile.key,
        tags: ['web-upload'], // Add a default tag to test the system
        description:
          file.type === 'application/pdf'
            ? 'PDF Document'
            : isPaivVideoEnabled && file.type.startsWith('video/')
            ? 'Video uploaded from web'
            : 'Image uploaded from web',
        sequence: processedImages.length,
        createdAt: new Date().toISOString(),
        isDeleted: false,
      };

      // Also maintain flexAttributes for backward compatibility
      if (!flexAttributes.photo) flexAttributes.photo = [];
      if (!flexAttributes.pictures) flexAttributes.pictures = [];

      // Only add to flexAttributes if we have a valid key
      if (uploadedFile.key && uploadedFile.key !== 'undefined') {
        flexAttributes.photo = [uploadedFile.key, ...flexAttributes.photo];
        flexAttributes.pictures = [
          uploadedFile.key,
          ...flexAttributes.pictures,
        ];
      }

      // Clean up any existing undefined values
      flexAttributes.photo = flexAttributes.photo.filter(
        (item: string) => item && item !== 'undefined' && item !== 'null'
      );
      flexAttributes.pictures = flexAttributes.pictures.filter(
        (item: string) => item && item !== 'undefined' && item !== 'null'
      );

      // Update local images for immediate UI feedback with the new file object
      // Only add if we have a valid key
      if (uploadedFile.key && uploadedFile.key !== 'undefined') {
        setLocalImages([
          uploadedFile.key,
          ...(localImages ||
            processedImages.map((img: ProcessedImage) => img.imageName)),
        ]);
      }

      // Save to backend via maintainDealV3
      deal.flexAttributes = JSON.stringify(flexAttributes);
      const ourDeal = {
        id: deal.id,
        name: deal.name,
        status: deal.status,
        imageName: deal.imageName,
        imageType: deal.imageType,
        currentStageID: deal.currentStageID,
        currentOwnerID: deal.currentOwnerID,
        currentOwnerType: deal.currentOwnerType,
        orgID: deal.orgID,
        isDeleted: false,
        flexAttributes: deal.flexAttributes,
        categoryID: deal.categoryID,
        tmst: newLocalToISODateFormat(getLocalDateTime()),
      };

      await API.graphql(
        graphqlOperation(mutations.maintainDealV3, {
          dealInfo: JSON.stringify(ourDeal),
        })
      );

      // TODO: Also save the fileObject as a DealFile with tags
      // This would require a createDealFile mutation
      console.log('File uploaded with metadata:', fileObject);

      onTriggerSelectedDealRefetch();

      if (!isOnKanban) {
        handleRefetchDeals();
      } else {
        handleRefetchDeals([deal.currentStageID], null, false);
      }

      setTimeout(() => {
        GamifyToast.success(
          file.type === 'application/pdf'
            ? 'PDF added to deal successfully'
            : isPaivVideoEnabled && file.type.startsWith('video/')
            ? 'Video added to deal successfully'
            : 'Image added to deal successfully'
        );
        setIsImageUploading(false);
      }, 1250);
    } catch (err) {
      console.error('onDropDealImage error: ', err);
      GamifyToast.error(
        'There was an error adding your file, please try again'
      );
      setIsImageUploading(false);
    }
  };

  // Download all files as a zip
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadAll = async () => {
    const validImages = processedImages.filter(
      (img) => img.imageName && img.imageName !== 'undefined'
    );

    if (validImages.length === 0) {
      GamifyToast.error('No files to download');
      return;
    }

    setIsDownloading(true);
    try {
      const zip = new JSZip();
      let successCount = 0;

      for (const img of validImages) {
        try {
          const signedUrl = await Storage.get(img.imageName);
          const response = await fetch(signedUrl);
          const blob = await response.blob();
          const fileName =
            img.imageName.split('/').pop() || `file-${img.index}`;
          zip.file(fileName, blob);
          successCount++;
        } catch (err) {
          console.error(`Failed to fetch ${img.imageName}:`, err);
        }
      }

      if (successCount === 0) {
        GamifyToast.error('Failed to download files');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const dealName = deal?.name || 'deal';
      const safeName = dealName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      saveAs(zipBlob, `${safeName}_files.zip`);

      GamifyToast.success(`Downloaded ${successCount} files`);
    } catch (error) {
      console.error('Error downloading files:', error);
      GamifyToast.error('Failed to create zip file');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle file click - different behavior for PDFs, videos, and images
  const handleFileClick = async (file: ProcessedImage) => {
    if (file.type === 'application/pdf') {
      // For PDFs, open in PDF viewer modal
      try {
        const signedURL = await Storage.get(file.imageName);
        setPdfUrl(signedURL);
        setPdfTitle(file.description || 'PDF Document');
        setShowPDFViewer(true);
      } catch (error) {
        console.error('Error opening PDF:', error);
        GamifyToast.error('Could not open PDF file');
      }
    } else if (isPaivVideoEnabled && isVideo(file)) {
      // For videos, open in video viewer modal (only if feature flag is enabled)
      try {
        const signedURL = await Storage.get(file.imageName);
        setVideoUrl(signedURL);
        setVideoTitle(file.description || 'Video');
        setShowVideoViewer(true);
      } catch (error) {
        console.error('Error opening video:', error);
        GamifyToast.error('Could not open video file');
      }
    } else {
      // For images, use the image carousel
      handleImageClick(file.index);
    }
  };

  return (
    <Box>
      {/* PDF Viewer Modal */}
      <Dialog
        open={showPDFViewer}
        onClose={() => setShowPDFViewer(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            marginTop: 18,
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            paddingTop: '60px', // Add padding to backdrop to account for navbar
          },
        }}
      >
        <DialogContent
          sx={{ padding: 0, overflow: 'hidden', position: 'relative' }}
        >
          {/* Floating close button */}
          <IconButton
            onClick={() => setShowPDFViewer(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              zIndex: 1000,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none', minHeight: '85vh' }}
              title={pdfTitle}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Video Viewer Modal */}
      <Dialog
        open={showVideoViewer}
        onClose={() => setShowVideoViewer(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            marginTop: 18,
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            paddingTop: '60px', // Add padding to backdrop to account for navbar
          },
        }}
      >
        <DialogContent
          sx={{ padding: 0, overflow: 'hidden', position: 'relative' }}
        >
          {/* Floating close button */}
          <IconButton
            onClick={() => setShowVideoViewer(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              zIndex: 1000,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {videoUrl && (
            <video
              src={videoUrl}
              controls
              autoPlay
              style={{
                width: '100%',
                height: '100%',
                minHeight: '85vh',
                backgroundColor: '#000',
              }}
              title={videoTitle}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Enhanced Title section */}
      <Box
        className={'selected-deal-section-title-container'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          padding: '12px 0',
          borderBottom: `1px solid ${theme.stroke}`,
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            className={'selected-deal-section-title-text'}
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: theme.text,
            }}
          >
            Uploaded Files
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Download All button */}
          {processedImages.length > 0 && (
            <Button
              onClick={handleDownloadAll}
              disabled={isDownloading}
              size="small"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={14} sx={{ color: theme.primary }} />
                ) : (
                  <DownloadIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                color: theme.primary,
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 500,
                '&:hover': { backgroundColor: `${theme.primary}10` },
              }}
            >
              {isDownloading ? 'Downloading...' : 'Download All'}
            </Button>
          )}

          {/* Upload button */}
          {showEdit && (
            <>
              {isImageUploading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 32,
                    height: 32,
                    backgroundColor: theme.primary,
                    borderRadius: '50%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <CircularProgress
                    style={{ color: 'white', width: 16, height: 16 }}
                  />
                </Box>
              ) : (
                <Dropzone onDrop={onDropDealImage}>
                  {({ getRootProps, getInputProps }) => (
                    <Box
                      {...getRootProps()}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 32,
                        height: 32,
                        backgroundColor: theme.primary,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: `${theme.primary}dd`,
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      <input {...getInputProps()} />
                      <AddIcon />
                    </Box>
                  )}
                </Dropzone>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Tag filter chips - positioned to align with title */}
      {allTags.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginLeft: '10%',
            marginRight: '15%',
            mb: 2,
            mt: 2,
          }}
        >
          <Chip
            label="All"
            onClick={() => setSelectedTag(null)}
            variant={selectedTag === null ? 'filled' : 'outlined'}
            sx={{
              backgroundColor:
                selectedTag === null ? theme.primary : 'transparent',
              color: selectedTag === null ? 'white' : theme.primary,
              borderColor: theme.primary,
              '&:hover': {
                backgroundColor:
                  selectedTag === null ? theme.primary : `${theme.primary}20`,
              },
            }}
            size="small"
          />
          {allTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => setSelectedTag(tag)}
              variant={selectedTag === tag ? 'filled' : 'outlined'}
              sx={{
                backgroundColor:
                  selectedTag === tag ? theme.primary : 'transparent',
                color: selectedTag === tag ? 'white' : theme.primary,
                borderColor: theme.primary,
                '&:hover': {
                  backgroundColor:
                    selectedTag === tag ? theme.primary : `${theme.primary}20`,
                },
              }}
              size="small"
            />
          ))}
        </Box>
      )}

      {/* Photo sections like mobile */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          marginLeft: '10%',
          marginRight: '15%',
          maxWidth: '740px',
          minWidth: '450px',
        }}
      >
        {/* New Images Section */}
        {filteredNewImages.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 4,
                  height: 16,
                  backgroundColor: theme.primary,
                  borderRadius: '2px',
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: theme.text,
                }}
              >
                New Files
              </Typography>
            </Box>
            <ImageList
              cols={5}
              gap={8}
              sx={{
                width: '100%',
                marginBottom: 0,
                marginTop: 0,
                overflow: 'visible',
              }}
            >
              {filteredNewImages.map((img: ProcessedImage, i: number) => (
                <ImageListItem key={i}>
                  <Button
                    onClick={() => handleFileClick(img)}
                    disableRipple={true}
                    sx={{
                      position: 'relative',
                      padding: 0,
                      minWidth: 'auto',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {img.type === 'application/pdf' ? (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          border: `2px solid ${theme.stroke}`,
                          cursor: 'pointer',
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundColor: '#f8f9fa',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: `2px solid ${theme.primary}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {img.imageName && img.imageName !== 'undefined' ? (
                          <iframe
                            src={`${img.imageName}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              transform: 'scale(0.9)',
                              transformOrigin: 'center',
                            }}
                            title="PDF Preview"
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.background2,
                              color: theme.text,
                              fontSize: '12px',
                            }}
                          >
                            No Preview
                          </Box>
                        )}
                        {/* PDF indicator overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            backgroundColor: theme.primary,
                            color: 'white',
                            borderRadius: '4px',
                            padding: '1px 4px',
                            fontSize: '8px',
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        >
                          PDF
                        </Box>
                      </Box>
                    ) : isPaivVideoEnabled && isVideo(img) ? (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          border: `2px solid ${theme.stroke}`,
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundColor: '#000',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: `2px solid ${theme.primary}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {img.imageName && img.imageName !== 'undefined' ? (
                          <video
                            src={img.imageName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.background2,
                              color: theme.text,
                              fontSize: '12px',
                            }}
                          >
                            No Preview
                          </Box>
                        )}
                        {/* Video play button overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 0,
                              height: 0,
                              borderLeft: '6px solid white',
                              borderTop: '4px solid transparent',
                              borderBottom: '4px solid transparent',
                              marginLeft: '1px',
                            }}
                          />
                        </Box>
                        {/* Video indicator overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            backgroundColor: theme.primary,
                            color: 'white',
                            borderRadius: '4px',
                            padding: '1px 4px',
                            fontSize: '8px',
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        >
                          VIDEO
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          border: `2px solid ${theme.stroke}`,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: `2px solid ${theme.primary}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {img.imageName && img.imageName !== 'undefined' ? (
                          <DisplayMedia
                            imageName={img.imageName}
                            imageType="img"
                            height="80px"
                            width="80px"
                            objectFit="cover"
                            borderRadius="12px"
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.background2,
                              color: theme.text,
                              fontSize: '12px',
                            }}
                          >
                            No Preview
                          </Box>
                        )}
                      </Box>
                    )}
                    {showEdit && (
                      <Box
                        sx={{
                          backgroundColor: '#060606',
                          display: 'flex',
                          position: 'absolute',
                          zIndex: 1,
                          top: 4,
                          right: 4,
                          boxSizing: 'border-box',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '16px',
                          width: '16px',
                          minWidth: '16px',
                          padding: 0,
                          borderRadius: '2000px',
                          cursor: 'pointer',
                          '&:hover': { border: 'white 1px solid' },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImageClick(img.index);
                        }}
                      >
                        {deletingImageIndex === img.index ? (
                          <CircularProgress size={8} sx={{ color: 'white' }} />
                        ) : (
                          <XIcon />
                        )}
                      </Box>
                    )}
                  </Button>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}

        {/* Uploaded Images Section */}
        {filteredUploadedImages.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ImageList
              cols={5}
              gap={8}
              sx={{
                width: '100%',
                marginBottom: 0,
                marginTop: 0,
                overflow: 'visible',
              }}
            >
              {filteredUploadedImages.map((img: ProcessedImage, i: number) => (
                <ImageListItem key={i}>
                  <Button
                    onClick={() => handleFileClick(img)}
                    disableRipple={true}
                    sx={{
                      position: 'relative',
                      padding: 0,
                      minWidth: 'auto',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {img.type === 'application/pdf' ? (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          border: `2px solid ${theme.stroke}`,
                          cursor: 'pointer',
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundColor: '#f8f9fa',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: `2px solid ${theme.primary}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {img.imageName && img.imageName !== 'undefined' ? (
                          <iframe
                            src={`${img.imageName}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              transform: 'scale(0.9)',
                              transformOrigin: 'center',
                            }}
                            title="PDF Preview"
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.background2,
                              color: theme.text,
                              fontSize: '12px',
                            }}
                          >
                            No Preview
                          </Box>
                        )}
                        {/* PDF indicator overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            backgroundColor: theme.primary,
                            color: 'white',
                            borderRadius: '4px',
                            padding: '1px 4px',
                            fontSize: '8px',
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        >
                          PDF
                        </Box>
                      </Box>
                    ) : isPaivVideoEnabled && isVideo(img) ? (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          border: `2px solid ${theme.stroke}`,
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundColor: '#000',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: `2px solid ${theme.primary}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {img.imageName && img.imageName !== 'undefined' ? (
                          <video
                            src={img.imageName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.background2,
                              color: theme.text,
                              fontSize: '12px',
                            }}
                          >
                            No Preview
                          </Box>
                        )}
                        {/* Video play button overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 0,
                              height: 0,
                              borderLeft: '6px solid white',
                              borderTop: '4px solid transparent',
                              borderBottom: '4px solid transparent',
                              marginLeft: '1px',
                            }}
                          />
                        </Box>
                        {/* Video indicator overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            backgroundColor: theme.primary,
                            color: 'white',
                            borderRadius: '4px',
                            padding: '1px 4px',
                            fontSize: '8px',
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        >
                          VIDEO
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '12px',
                          border: `2px solid ${theme.stroke}`,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            border: `2px solid ${theme.primary}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        {img.imageName && img.imageName !== 'undefined' ? (
                          <DisplayMedia
                            imageName={img.imageName}
                            imageType="img"
                            height="80px"
                            width="80px"
                            objectFit="cover"
                            borderRadius="12px"
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme.background2,
                              color: theme.text,
                              fontSize: '12px',
                            }}
                          >
                            No Preview
                          </Box>
                        )}
                      </Box>
                    )}
                    {showEdit && (
                      <>
                        {/* Edit button */}
                        <Box
                          sx={{
                            backgroundColor: theme.primary,
                            display: 'flex',
                            position: 'absolute',
                            zIndex: 1,
                            top: 4,
                            right: 24,
                            boxSizing: 'border-box',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '16px',
                            width: '16px',
                            minWidth: '16px',
                            padding: 0,
                            borderRadius: '2000px',
                            cursor: 'pointer',
                            '&:hover': { border: 'white 1px solid' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnnotatorPhoto({
                              imageName: img.imageName,
                              tags: img.tags || [],
                              description: img.description || '',
                              fileId: img.fileId,
                              type: img.type,
                            });
                            setShowPhotoAnnotator(true);
                          }}
                        >
                          <EditIcon sx={{ fontSize: 10, color: 'white' }} />
                        </Box>
                        {/* Delete button */}
                        <Box
                          sx={{
                            backgroundColor: '#060606',
                            display: 'flex',
                            position: 'absolute',
                            zIndex: 1,
                            top: 4,
                            right: 4,
                            boxSizing: 'border-box',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '16px',
                            width: '16px',
                            minWidth: '16px',
                            padding: 0,
                            borderRadius: '2000px',
                            cursor: 'pointer',
                            '&:hover': { border: 'white 1px solid' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImageClick(img.index);
                          }}
                        >
                          {deletingImageIndex === img.index ? (
                            <CircularProgress
                              size={8}
                              sx={{ color: 'white' }}
                            />
                          ) : (
                            <XIcon />
                          )}
                        </Box>
                      </>
                    )}
                  </Button>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}

        {/* Uploading Images Section */}
        {filteredUploadingImages.length > 0 && (
          <Box
            sx={{
              mb: 3,
              backgroundColor: theme.background2,
              borderRadius: 2,
              padding: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: theme.text,
                }}
              >
                {`Uploading Photos (${filteredUploadingImages.length})`}
              </Typography>
            </Box>
            <ImageList
              cols={5}
              gap={8}
              sx={{
                width: '100%',
                marginBottom: 0,
                marginTop: 0,
                overflow: 'visible',
              }}
            >
              {filteredUploadingImages.map((img: ProcessedImage, i: number) => (
                <ImageListItem key={i}>
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      onClick={() => handleFileClick(img)}
                      disableRipple={true}
                      sx={{
                        position: 'relative',
                        padding: 0,
                        minWidth: 'auto',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {img.type === 'application/pdf' ? (
                        <Box
                          sx={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${theme.stroke}`,
                            cursor: 'pointer',
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: '#f8f9fa',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              border: `2px solid ${theme.primary}`,
                              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            },
                          }}
                        >
                          <iframe
                            src={`${img.imageName}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              transform: 'scale(0.9)',
                              transformOrigin: 'center',
                            }}
                            title="PDF Preview"
                          />
                          {/* PDF indicator overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              backgroundColor: theme.primary,
                              color: 'white',
                              borderRadius: '4px',
                              padding: '1px 4px',
                              fontSize: '8px',
                              fontWeight: 600,
                              zIndex: 1,
                            }}
                          >
                            PDF
                          </Box>
                        </Box>
                      ) : isPaivVideoEnabled && isVideo(img) ? (
                        <Box
                          sx={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${theme.stroke}`,
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: '#000',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              border: `2px solid ${theme.primary}`,
                              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            },
                          }}
                        >
                          <video
                            src={img.imageName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            muted
                            playsInline
                            preload="metadata"
                          />
                          {/* Video play button overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid white',
                                borderTop: '3px solid transparent',
                                borderBottom: '3px solid transparent',
                                marginLeft: '1px',
                              }}
                            />
                          </Box>
                          {/* Video indicator overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              backgroundColor: theme.primary,
                              color: 'white',
                              borderRadius: '4px',
                              padding: '1px 4px',
                              fontSize: '8px',
                              fontWeight: 600,
                              zIndex: 1,
                            }}
                          >
                            VIDEO
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '12px',
                            border: `2px solid ${theme.stroke}`,
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              border: `2px solid ${theme.primary}`,
                              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            },
                          }}
                        >
                          <DisplayMedia
                            imageName={img.imageName}
                            imageType="img"
                            height="80px"
                            width="80px"
                            objectFit="cover"
                            borderRadius="12px"
                          />
                        </Box>
                      )}
                    </Button>
                    {/* Upload overlay for uploading images */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '80px',
                        height: '80px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        padding: 1,
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                        }}
                      >
                        <Box
                          component="svg"
                          viewBox="0 0 24 24"
                          sx={{
                            width: 16,
                            height: 16,
                            fill: 'none',
                            stroke: theme.primary,
                            strokeWidth: 2,
                            strokeLinecap: 'round',
                            strokeLinejoin: 'round',
                          }}
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </Box>
                      </Box>
                    </Box>
                    {showEdit && (
                      <Box
                        sx={{
                          backgroundColor: '#060606',
                          display: 'flex',
                          position: 'absolute',
                          zIndex: 1,
                          top: 4,
                          right: 4,
                          boxSizing: 'border-box',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '16px',
                          width: '16px',
                          minWidth: '16px',
                          padding: 0,
                          borderRadius: '2000px',
                          cursor: 'pointer',
                          '&:hover': { border: 'white 1px solid' },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImageClick(img.index);
                        }}
                      >
                        {deletingImageIndex === img.index ? (
                          <CircularProgress size={8} sx={{ color: 'white' }} />
                        ) : (
                          <XIcon />
                        )}
                      </Box>
                    )}
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}

        {/* Empty state */}
        {filteredNewImages.length === 0 &&
          filteredUploadedImages.length === 0 &&
          filteredUploadingImages.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                padding: 4,
                border: `1px dashed ${theme.stroke}`,
                borderRadius: 2,
                backgroundColor: theme.background2,
              }}
            >
              <Typography variant="body2" sx={{ color: theme.textLight }}>
                {selectedTag
                  ? `No files found with tag "${selectedTag}"`
                  : 'No files uploaded yet'}
              </Typography>
            </Box>
          )}
      </Box>

      {/* Delete Confirmation Modal */}
      <DynamicConfirmModal
        showDynamicConfirmModal={showConfirmDeleteImage}
        setShowDynamicConfirmModal={setShowConfirmDeleteImage}
        zIndex={100002}
        title="Are you sure you want to remove this file?"
        subtitle="This action cannot be undone."
        leftAlignText={true}
        confirmButtonColor="#E35050"
        isSaving={deletingImageIndex !== null}
        onCancel={() => {
          setShowConfirmDeleteImage(false);
          setImageIndexToDelete(null);
        }}
        onConfirm={handleConfirmDeleteImage}
      />

      {/* Photo Annotator Modal */}
      <PhotoAnnotator
        open={showPhotoAnnotator}
        onClose={() => {
          setShowPhotoAnnotator(false);
          setAnnotatorPhoto(null);
        }}
        photo={annotatorPhoto}
        dealId={deal?.id || ''}
        onSave={() => {
          // Only refetch the selected deal (avoid refetching the full deals list)
          onTriggerSelectedDealRefetch();
        }}
      />
    </Box>
  );
}
